import React, { Component } from 'react';
import { connect } from 'react-redux';
import ScrollArea from 'react-scrollbar';
import Toggle from 'material-ui/Toggle';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';

import TextField from '../../components/basic/TextField';
import Button from '../../components/basic/Button';
import ModelPicker from '../../components/basic/ModelPicker';
import { Section, SectionItem } from '../../components/basic/section';
import LLMChat from '../../components/chats/LLMChat';
import { DEFAULT_LLM_MODEL } from '../../constants/Constants';

class PromptTesterContainer extends Component {
  constructor(props) {
    super(props);

    // Initialize state
    this.state = {
      errors: {},
      // Form state
      formData: {
        type: 'CANDIDATE',
        entityId: '26299d39-212d-46c2-a9bf-9b588138347c',
        promptTemplate: `<#ftl strip_whitespace=true />
I want you to analyze a candidate's profile and return a structured fit summary.
Language: clear, friendly, yet professional, avoid too scientific words, minimize ambiguous words, high school graduate level
Audience: recruiter, hiring manager or a company CEO
Text goal: Understand quickly
Text formatting: short sentences, simple paragraphs, avoid filler words or long introductory phrases.
Interpreting results: where too complicated or theoretical terms appear, feel free to describe it in a more human or practical way. 
Be careful with too harsh statements regarding the candidate. Where score indicates missing competence or the candidate being unable to do something, wrap it into the way that they less demonstrated the capability and might presume shortcoming. 

Based on this, generate a result with the following sections:

Candidate Fit Summary

A short paragraph summarizing the candidate's strengths, challenges, and the type of environment where they may thrive. Mention any concerns or areas for development

✅ Strengths:

Bullet points summarizing strengths relevant to the role or team. (preset relevant, high fit) 

⚠️ Watch out for:

Bullet points of potential risks or development areas. (preset relevant, low fit)

📌 Solid base to build on:
Bullet points of average scoring areas (preset relevant, medium fit, solid base)

<#-- Hidden potential: (display only if preset irrelevant metric is high range)-->

🎯 Ask in interview:
Suggest interview questions to clarify gaps or explore potential. (for preset relevant metrics if fit low). 

🧩 If hired:
Recommendations for support, environment, or development if the candidate joins. Give hints on how to best onboard and what to focus on based on their profile.

<#function findMetric metricId assessments>
    <#if assessments?is_sequence>
        <#list assessments as assessment>
            <#if assessment.metrics?is_sequence && assessment.metrics?has_content>
                <#list assessment.metrics as metric>
                  <#if metric.id?string == metricId?string>
                    <#return metric>
                  </#if>
                </#list>
            </#if>
        </#list>
    </#if>
    <#return null>
</#function>

<#macro formatRank rank>
<#compress>
<#switch rank>
<#case 'LOW'>Low<#break>
<#case 'HIGH'>High<#break>
<#case 'MID'>Medium<#break>
<#case 'MID_LOW'>Medium Low<#break>
<#case 'MID_HIGH'>Medium High<#break>
<#default>Unknown
</#switch>
</#compress>
</#macro>

<#macro formatMetricRank rank metric>
<#compress>
<#if metric.id?contains("cf")>Preference: <#switch rank>
<#case 'LOW'>\${metric.oppositeLabel} (Strong)<#break>
<#case 'HIGH'>\${metric.label} (Strong)<#break>
<#case 'MID_LOW'>\${metric.oppositeLabel} (Slight)<#break>
<#case 'MID_HIGH'>\${metric.label} (Slight)<#break>
<#default>Unknown
</#switch>
<#else>Manifestation: <#switch rank>
<#case 'LOW'>Low<#break>
<#case 'HIGH'>High<#break>
<#case 'MID'>Medium<#break>
<#case 'MID_LOW'>Medium Low<#break>
<#case 'MID_HIGH'>Medium High<#break>
<#default>Unknown
</#switch>
</#if>
</#compress>
</#macro>

<#macro formatWeight weight>
<#compress>
<#if weight < 2>Low<#elseif weight < 5>Medium<#else>High</#if>
</#compress>
</#macro>

<#macro formatPresetRoleType type>
<#compress>
<#switch type>
  <#case 'SALES_REPRESENTATIVE'>Sales Specialist - Ideal for roles focused on direct selling and closing deals<#break>
  <#case 'CUSTOMER_CARE_REPRESENTATIVE'>Customer Care Specialist - Ideal for roles focused on assisting customers, handling inquiries, and ensuring a positive experience<#break>
  <#case 'TECH_SUPPORT'>Technical Support & Product Specialist - Ideal for roles that assist customers with technical issues, troubleshoot problems, handle product requests, and report bugs<#break>
  <#case 'ANALYTICS_SPECIALIST'>Data & Analytics Specialist - Ideal for roles focused on collecting, analyzing, and interpreting data to support business decisions<#break>
  <#case 'MARKETING_SPECIALIST'>Marketing Specialist - Ideal for roles focused on creating campaigns, analyzing market trends, and driving brand awareness<#break>
  <#case 'SOFTWARE_SPECIALIST'>Software & IT Specialist - Ideal for roles focused on developing, maintaining, and optimizing software or IT systems<#break>
  <#case 'PROJECT_COORDINATOR'>Project & Account Coordinator - Ideal for roles that manage projects, coordinate tasks, and maintain client relationships<#break>
  <#case 'OPERATIONS_SPECIALIST'>Office & Operations Specialist - Ideal for roles that support daily business operations, administrative tasks, and process coordination<#break>
  <#default>Unknown (\${type})<#break>
</#switch>
</#compress>
</#macro>

<#if preset??>
The position preset configuration:
- role type: <@formatPresetRoleType type=preset.roleType />
- metrics relevant for fit calculation:
<#list preset.metrics as metric>
  <#assign m = findMetric(metric.metricId, assessments)>
  - Metric name: \${m.label}<#if m.oppositeLabel??> vs \${m.oppositeLabel}</#if> 
</#list>
<#if preset.complementaryMetrics?is_sequence && preset.complementaryMetrics?has_content>
- complementary metrics (look for hidden potential in those):
<#list preset.complementaryMetrics as metric>
  <#assign m = findMetric(metric.metricId, assessments)>
  - Metric name: \${m.label}<#if m.oppositeLabel??> vs \${m.oppositeLabel}</#if> 
</#list>
</#if>
<#else>
There is no position preset available, treat all metrics as equally important.
</#if>

The participant assessment results:
<#assign p = participants[0]>
<#list p.assessmentResults as result>

\${result?index + 1}) Assessment: <#switch result.assessmentType><#case 'OFFICE_DAY'>The Office Day - testing transferable competencies, in the form of simulation game<#break><#case 'GAME_CHANGER'>The Game Changer - testing managerial competencies<#break><#case 'CULTURE_FIT'>Culture Fit - evaluating work-related preferences<#break></#switch>

<#-- Access metrics for this assessment -->
Metrics:
<#list result.metrics as metric>

    <#assign m = findMetric(metric.metric.id, assessments)>
    \${metric?index + 1}) Metric: \${m.label}<#if m.oppositeLabel??> vs \${m.oppositeLabel}</#if>
    <#if m.operationalization??>Operationalization: \${m.operationalization}</#if>
    <#nt><@formatMetricRank metric=m rank=metric.rank />
    <#-- Access fit information if available -->
    <#if metric.presetFit??>
    Metric Fit: \${metric.presetFit.fit * 100}% (<@formatRank rank=metric.presetFit.rank />)
    Metric Weight: <@formatWeight weight=metric.presetFit.weight />
    <#if metric.presetFit.interpretation??>Interpretation:
    <#if metric.presetFit.interpretation.text??>\${metric.presetFit.interpretation.text}</#if>
    <#if metric.presetFit.interpretation.bulletsDisclaimer??>\${metric.presetFit.interpretation.bulletsDisclaimer}</#if>
    <#if metric.presetFit.interpretation.bullets??>
    <#list metric.presetFit.interpretation.bullets as bullet>
      - \${bullet}
    </#list>
    </#if>
    </#if>
    <#elseif metric.interpretation??>Interpretation:
    <#if metric.interpretation.text??>Text: \${metric.interpretation.text}</#if>
    <#if metric.interpretation.label??>Label: \${metric.interpretation.label}</#if>
    <#if metric.interpretation.bullets??>
    Bullets:
    <#list metric.interpretation.bullets as bullet>
      - \${bullet}
    </#list>
    </#if>
    </#if>
</#list>
</#list>

<#-- Access total fit across all assessments -->
<#if p.fit??>
Calculated Position Fit: \${p.fit.fit * 100}%
</#if>
`,
        dryRun: true,
        language: 'en',
        maxTokens: 8192,
        temperature: 0,
        model: DEFAULT_LLM_MODEL.value,
        location: '',
        projectId: 'c07d9bb8-06d1-436b-a787-1b916d64e63b'
      },
      // Results state
      result: {
        digest: `Based on the provided assessment data for participant f973b528-f305-4bb6-89b9-6e97242402ab, here's a breakdown of their potential suitability for a Sales Representative role and key considerations:

**Overall Strengths:**

*   **Strong Problem Solving:**  Scores high (10) indicating a comprehensive approach to tasks and challenges.
*   **Excellent Information Processing:**  Quickly understands and applies information.
*   **Customer-Centric:**  High customer orientation (9) and is able to quickly identify the customer's needs, takes their side and acts in their interest.
*   **Self-Aware & Learns Quickly:** High self-knowledge (10) indicating the candidate accurately assesses their strengths and weaknesses, learns from experience, and is open to improvement.
*   **Adaptable Communicator:**  Effective communication (7), able to adapt their style to others.
*    **Numeracy:** Is able to work with numbers, navigate and sort data.

**Areas of Concern & Considerations:**

*   **Culture Fit Mismatch (Overall: 0.35):** This is a significant concern, as it suggests potential friction between the candidate's preferences and a typical company culture.  The main drivers behind this score are:
    *   **cfIndependence (Value: 4, Rank: HIGH, Fit: 0):** This is the biggest red flag. The candidate *needs* to work independently and have space to take initiative. The *opposite* of this is a preference for hierarchy, clear guidance, and support.  This directly contradicts the need for independence.
    *   **cfPerformance (Value: 2, Rank: MID\\_LOW, Fit: 0.33):** Prefers a team with closer ties, where relationships are valued. This score might be detrimental to some sales role.
    *   **cfChaos (Value: 2, Rank: MID\\_LOW, Fit: 0.67):** Appreciates order, predictability, clear rules and processes. This preference clashes with the fast-paced, adaptable environment that is common in Sales.

*   **Integrity (Value: 7):**  While "Noticeable," the target range is 9-10.  This isn't a deal-breaker but it is important to discuss during the interview to understand how they manage their integrity.

**Detailed Analysis & Recommendations:**

1.  **Deep Dive into Independence Preference:** The discrepancy between *needing* independence and preferring a hierarchical structure is crucial.  The interview needs to thoroughly explore *why* the candidate values independence.  Possible explanations include:
    *   **Desire for Autonomy:**  They want control over *how* they achieve results, not necessarily a lack of oversight or guidance. If autonomy is the key, it might be possible to structure the role in a way that provides the *feeling* of independence.
    *   **Frustration with Micromanagement:**  They've had negative experiences with overly controlling managers.  This is an easier fix - assuring them of a hands-off, trust-based management style.
    *   **Discomfort with Teamwork:**  They genuinely prefer solo work and avoid collaboration.  This is a major problem for a sales role that requires internal coordination.

2.  **Clarify Performance/Relationship Balance:**  Sales requires a results-oriented approach.  Explore how the candidate balances building relationships with achieving sales targets.  Do they understand that strong relationships can *drive* sales?  Their preference for "closer ties" could be a strength if they can leverage those relationships to close deals.

3.  **Address Integrity:** Because integrity is a high priority for the role, explore scenarios during the interview to assess their ethical decision-making and commitment to honesty.

4.  **Culture Fit Questions are Critical:**  The low overall culture fit score makes this a high-risk hire unless you can definitively address the concerns around independence, performance, and chaos. Prepare targeted questions to assess:
    *   How they handle ambiguity and change.
    *   Their preferred communication style and how they navigate workplace relationships.
    *   Their reaction to structure and the need to follow processes.
    *   What they consider to be an ideal management style.

5.  **Weigh the Strengths:**  The candidate has several significant strengths. The critical question is: Do these strengths outweigh the potential risks associated with the culture fit issues? If the answers to all the above questions show that candidate might be able to thrive in the role, they might be a good fit.

**In summary:** This candidate presents a mixed picture. Their strong problem-solving, customer orientation, and self-awareness are valuable assets for a sales role. However, the significant concerns around culture fit, particularly the independence paradox, must be addressed through thorough interviewing before making a hiring decision. Be prepared to adapt the role to their preferences if necessary, or to accept that their needs might be incompatible with the team.
`,
        prompt: ''
      },
      // Loading state
      loading: false,
      chat: []
    };
  }

  componentDidMount() {
    this.loadPrompts();
  }

  // Handle form input changes
  changeFormData = (name, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  };

  handleChange = e => {
    const { name, value, type, checked } = e.target;
    this.changeFormData(name, type === 'checkbox' ? checked : value);
  };
  handleTypeChange = (e, idx, value) => this.changeFormData('type', value);
  handleModelChange = (e, idx, value) => this.changeFormData('model', value);

  loadPrompts = async () => {
    const { auth } = this.props;
    try {
      this.setState({ promptsLoading: true });
      const response = await fetch('/api/v1/admin/ai/prompts', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.access_token}`
        }
      });
      const { items } = await response.json();
      this.setState({ prompts: items });
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      this.setState({ promptsLoading: false });
    }
  };

  handleSavePrompt = async () => {
    const { auth } = this.props;
    const {
      formData: { promptId, language, promptTemplate, model, maxTokens, temperature, location }
    } = this.state;
    try {
      this.setState({ promptSaving: true });
      const prompt = this.state.prompts.find(p => p.id === promptId);
      if (!prompt) {
        throw new Error('Prompt not found');
      }
      const { [language]: oldTemplate, ...templates } = prompt.data.templates || {};
      const template = promptTemplate && promptTemplate.trim();
      if (template) {
        templates[language] = template;
      }
      await fetch(`/api/v1/admin/ai/prompts/${promptId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.access_token}`
        },
        body: JSON.stringify({
          ...prompt,
          data: {
            ...prompt.data,
            templates,
            params: {
              ...prompt.data.params,
              model,
              maxTokens,
              temperature,
              location: location && location.trim() !== '' ? location : null
            }
          }
        })
      });
      this.loadPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      this.setState({ promptSaving: false });
    }
  };

  handlePromptChange = (e, idx, value) => {
    const {
      prompts,
      formData: { language }
    } = this.state;
    const prompt = prompts.find(p => p.id === value);
    this.changeFormData('promptId', value);
    if (!prompt) {
      return;
    }
    const { data } = prompt;
    const { params, templates } = data;
    const template = templates[language];
    if (template) {
      this.changeFormData('promptTemplate', template);
    } else {
      const tk = Object.keys(templates)[0];
      if (!tk) {
        this.changeFormData('promptTemplate', '');
      } else {
        this.changeFormData('promptTemplate', templates[tk]);
        this.changeFormData('language', tk);
      }
    }
  };
  handleLanguageChange = (e, idx, value) => {
    const {
      prompts,
      formData: { promptId }
    } = this.state;
    this.changeFormData('language', value);
    const prompt = promptId ? prompts.find(p => p.id === promptId) : null;
    if (!prompt) {
      return;
    }
    const template = prompt.data.templates[value];
    this.changeFormData('promptTemplate', template || '');
  };

  // Handle form submission
  handleSubmit = async e => {
    const { auth } = this.props;

    e.preventDefault();
    this.setState({ loading: true, errors: {}, chat: [] });
    const { formData } = this.state;
    try {
      const candidateId = formData.entityId;
      if (!candidateId) {
        this.setState({
          errors: {
            entityId: 'Candidate ID is required'
          }
        });
        throw new Error('Candidate ID is required');
      }
      let url;
      let bodyPart = {};
      if (formData.type === 'CANDIDATE') {
        url = `/api/v1/admin/ai/digests/candidates/${candidateId}/generate`;
      } else if (formData.type === 'PIPELINE') {
        url = `/api/v1/admin/ai/digests/pipeline/generate`;
        bodyPart = {
          input: formData.pipelineInput
        };
      } else if (formData.type === 'ECHO_PULSE') {
        url = `/api/v1/admin/ai/digests/echo-pulse/generate`;
        bodyPart = {
          projectId: formData.projectId,
          teamIds: (formData.teamId || '')
            .split(',')
            .map(id => id.trim())
            .filter(id => !!id)
        };
      } else {
        throw new Error(`Invalid type - ${formData.type}`);
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.access_token}`
        },
        body: JSON.stringify({
          promptTemplate: formData.promptTemplate,
          dryRun: formData.dryRun,
          language: formData.language,
          maxTokens: formData.maxTokens,
          temperature: formData.temperature,
          model: formData.model,
          location: formData.location && formData.location.trim() !== '' ? formData.location : null,
          ...bodyPart
        })
      });

      const { payload, error, errorMessage } = await response.json();
      const err = error || errorMessage;

      if (err) {
        throw new Error(err);
      }

      this.setState({
        result: {
          digest: payload.digest || '',
          digestHtml: payload.digestHtml || '',
          prompt: payload.prompt || ''
        },
        chat: formData.dryRun
          ? []
          : [
              {
                role: 'USER',
                content: payload.prompt,
                hidden: true
              },
              {
                role: 'ASSISTANT',
                content: payload.digest,
                contentHtml: payload.digestHtml
              }
            ]
      });
    } catch (error) {
      console.error('Error testing prompt:', error);
      this.setState({
        result: {
          error: `Error: ${error.message}`,
          digest: '',
          prompt: ''
        }
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { formData, result, loading, chat, errors, promptsLoading, promptSaving } = this.state;

    return (
      <form className="Form" onSubmit={this.handleSubmit}>
        <ScrollArea>
          <div className="Form-content-container">
            <div className="container">
              <div className="row">
                <div className="col-md-6">
                  <Section title="Prompt testing">
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <div className="row">
                        <div className="col-sm-6">
                          <SelectField
                            id="promptId"
                            name="promptId"
                            value={formData.promptId}
                            onChange={this.handlePromptChange}
                            floatingLabelText={'Prompt'}
                            floatingLabelFixed
                            hintText={'Prompt'}
                            fullWidth
                            style={{ fontSize: '14px' }}
                          >
                            <MenuItem value="" primaryText="" />
                            {(this.state.prompts || []).map(p => (
                              <MenuItem key={p.id} value={p.id} primaryText={p.name} />
                            ))}
                          </SelectField>
                        </div>
                        <div className="col-sm-6">
                          <SelectField
                            id="language"
                            name="language"
                            value={formData.language}
                            onChange={this.handleLanguageChange}
                            floatingLabelText={'Language'}
                            floatingLabelFixed
                            hintText={'Language'}
                            fullWidth
                            style={{ fontSize: '14px' }}
                          >
                            <MenuItem key="en" value="en" primaryText="English" />
                            <MenuItem key="cs" value="cs" primaryText="Česky" />
                          </SelectField>
                        </div>
                      </div>
                    </SectionItem>

                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        id="promptTemplate"
                        name="promptTemplate"
                        value={formData.promptTemplate}
                        onChange={this.handleChange}
                        label="Prompt template"
                        hintText="Enter your prompt template here"
                        fullWidth
                        multiLine
                        rows={10}
                        rowsMax={10}
                        inputStyle={{ fontFamily: 'monospace', fontSize: '10px', lineHeight: '14px' }}
                      />
                    </SectionItem>

                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <SelectField
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={this.handleTypeChange}
                        floatingLabelText={'Type'}
                        floatingLabelFixed
                        hintText={'Type'}
                        fullWidth
                        style={{ fontSize: '14px' }}
                      >
                        <MenuItem key="CANDIDATE" value="CANDIDATE" primaryText="Candidate" />
                        <MenuItem key="ECHO_PULSE" value="ECHO_PULSE" primaryText="Echo Pulse" />
                        <MenuItem key="PIPELINE" value="PIPELINE" primaryText="Pipeline" />
                      </SelectField>

                      {formData.type === 'PIPELINE' && (
                        <TextField
                          id="pipelineInput"
                          name="pipelineInput"
                          value={formData.pipelineInput}
                          onChange={this.handleChange}
                          label="Input"
                          hintText="Enter your input here (and don't forget to place ${input} placeholder in the prompt template)"
                          fullWidth
                          multiLine
                          rows={10}
                          rowsMax={10}
                          inputStyle={{ fontFamily: 'monospace', fontSize: '10px', lineHeight: '14px' }}
                        />
                      )}
                      {formData.type === 'CANDIDATE' && (
                        <TextField
                          type="text"
                          id="entityId"
                          name="entityId"
                          value={formData.entityId}
                          onChange={this.handleChange}
                          fullWidth
                          label="Candidate ID"
                          hintText="Enter candidate ID"
                          errorText={errors.entityId}
                        />
                      )}
                      {formData.type === 'ECHO_PULSE' && (
                        <TextField
                          type="text"
                          id="projectId"
                          name="projectId"
                          value={formData.projectId}
                          onChange={this.handleChange}
                          fullWidth
                          label="Project ID"
                          hintText="Enter project ID"
                          errorText={errors.projectId}
                        />
                      )}
                      {formData.type === 'ECHO_PULSE' && (
                        <TextField
                          type="text"
                          id="teamId"
                          name="teamId"
                          value={formData.teamId}
                          onChange={this.handleChange}
                          fullWidth
                          label="Team ID"
                          hintText="Enter team ID"
                          errorText={errors.teamId}
                        />
                      )}
                    </SectionItem>

                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <div className="row">
                        <div className="col-sm-6">
                          <TextField
                            id="maxTokens"
                            name="maxTokens"
                            value={formData.maxTokens}
                            onChange={this.handleChange}
                            fullWidth
                            label="Max tokens"
                            hintText="Enter max tokens"
                            helperText="Maximum number of tokens to generate"
                            errorText={errors.maxTokens}
                            type="number"
                            min={1}
                            max={65535}
                          />
                        </div>
                        <div className="col-sm-6">
                          <TextField
                            id="temperature"
                            name="temperature"
                            value={formData.temperature}
                            onChange={this.handleChange}
                            fullWidth
                            label="Temperature"
                            hintText="Enter temperature"
                            helperText="Temperature for the model"
                            errorText={errors.temperature}
                            type="number"
                            min={0}
                            max={2}
                            step={0.05}
                          />
                        </div>
                      </div>

                      <ModelPicker
                        id="model"
                        name="model"
                        value={formData.model}
                        onChange={value => this.handleModelChange(null, null, value)}
                        floatingLabelText={'Model'}
                        floatingLabelFixed
                        hintText={'Model'}
                        fullWidth
                        style={{ fontSize: '14px' }}
                      />

                      <TextField
                        id="location"
                        name="location"
                        value={formData.location || ''}
                        onChange={this.handleChange}
                        fullWidth
                        label="Location"
                        hintText="Enter location (optional)"
                      />
                    </SectionItem>
                  </Section>
                </div>

                <div className="col-md-6">
                  <Section title="Results">
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        id="prompt"
                        name="prompt"
                        value={result.prompt}
                        label="Prompt"
                        hintText="The prompt will be shown here"
                        fullWidth
                        multiLine
                        rows={10}
                        rowsMax={10}
                        inputStyle={{ fontFamily: 'monospace', fontSize: '10px', lineHeight: '14px' }}
                        textareaStyle={{
                          backgroundColor: 'rgba(70, 97, 124, 0.05)',
                          borderRadius: '4px',
                          padding: '8px'
                        }}
                      />
                      {result.error && <div className="Text-error Text-regular">{result.error}</div>}
                    </SectionItem>
                    <SectionItem>
                      <h4
                        style={{
                          marginBottom: '6px',
                          fontSize: '10.5px',
                          fontWeight: 'normal',
                          color: 'rgb(158, 158, 158)'
                        }}
                      >
                        Digest
                      </h4>
                      <div style={{ border: '1px solid #f0f0f0', borderRadius: '5px' }}>
                        <LLMChat chat={chat} enabled={chat.length > 0} params={formData} />
                      </div>
                    </SectionItem>
                  </Section>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="Form-buttons-container" style={{ overflow: 'hidden' }}>
          <div
            className="Form-buttons flex flex1 flex-align-center"
            style={{ gap: '1rem', justifyContent: 'flex-end' }}
          >
            <div>
              <Button
                label="Save Prompt"
                secondary
                raised
                loading={promptsLoading || promptSaving}
                onClick={this.handleSavePrompt}
                disabled={!formData.promptId}
              />
            </div>
            <div className="flex1">
              <Toggle
                name={'dryRun'}
                onToggle={this.handleChange}
                label={'Dry Run'}
                toggled={formData.dryRun}
                style={{ fontSize: '14px' }}
              />
            </div>
            <div>
              <Button label="Test Prompt" primary raised loading={loading} type="submit" />
            </div>
          </div>
        </div>
      </form>
    );
  }
}

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(PromptTesterContainer);



// WEBPACK FOOTER //
// ./src/containers/private/PromptTesterContainer.js